import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, CalendarDays, FileText, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { groupEpisodes } from '../lib/longformGroups';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import StageChip from '../ui/StageChip';
import ListRow from '../ui/ListRow';
import Table from '../ui/Table';
import Select from '../ui/Select';
import Icon from '../ui/Icon';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Skeleton from '../ui/Skeleton';
import { STAGES } from '../ui/stages';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';

// DESIGN rev 2.1 §3.1: the Dashboard is a work list (Needs me, Due this week, Blocked), built only from the data
// this page has always loaded. The per-project tables below keep what the old page showed that fits no list.

function getPostedSince(filter) {
  const now = new Date();
  if (filter === 'week') {
    const d = new Date(now);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); // back to Monday
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  }
  if (filter === 'month') {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }
  return null; // all time: no filter
}

function timeAgo(dateString) {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} days ago`;
}

const ymd = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const dayOf = v => (v ? String(v).slice(0, 10) : null);           // @db.Date fields arrive as ISO midnight UTC
const shortDate = s => new Date(`${s}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

const ENTITY_LABEL = { script: 'Script', live_production: 'Live production', ai_production: 'AI production' };
const QUAL_ROLES = ['Admin', 'Power User', 'Qual Reviewer'];          // the roles the Qual page lets act (canEdit)
const POSTED_OPTIONS = [{ value: 'week', label: 'This week' }, { value: 'month', label: 'This month' }, { value: 'all', label: 'All time' }];
const POSTED_PHRASE = { week: 'this week', month: 'this month', all: 'in total' };
const COLS = ['scripted', 'filming', 'filmed', 'editing', 'qual', 'approved', 'posted'];

// An audit-log value as the system shows it: a production status is a stage chip (Incomplete is a condition);
// a script's qual state is its condition badge, or plain words for the states that are not conditions.
const SCRIPT_CONDITION = { 'I/P': 'In progress', 'Needs Review': 'Needs review', 'Needs Revision': 'Needs revision' };
function AuditValue({ entityType, value }) {
  if (!value) return null;
  if (entityType === 'script') {
    if (SCRIPT_CONDITION[value]) return <Badge condition={SCRIPT_CONDITION[value]} />;
    return <span>{clean(value)}</span>;
  }
  if (value === 'Incomplete') return <Badge condition="Incomplete" />;
  if (STAGES.includes(value)) return <StageChip stage={value} />;
  return <span>{clean(value)}</span>;
}

function depthLevel(count) {
  if (count <= 3) return { tone: 'danger', label: 'Low' };
  if (count <= 7) return { tone: 'warn', label: 'Mid' };
  return { tone: 'ok', label: 'Good' };
}

function WorkSection({ title, note, loading, rows, empty }) {
  return (
    <section className="ui-section" aria-label={title}>
      <div className="ui-section__head">
        <h2 className="ui-section__title">{title}</h2>
        {!loading && <span className="ui-text-3 ui-dense ui-num">{rows.length}</span>}
      </div>
      {note && <p className="ui-section__note">{note}</p>}
      <div className="ui-region">
        {loading
          ? <div className="ui-list">{[0, 1, 2].map(i => <ListRow key={i} title={<Skeleton width="40%" />} />)}</div>
          : rows.length
            ? <div className="ui-list">{rows.map(r => <ListRow key={r.key} {...r.row} />)}</div>
            : <EmptyState {...empty} />}
      </div>
    </section>
  );
}

function SortHead({ col, label, sort, setSort }) {
  const active = sort.col === col;
  return (
    <button type="button" className="ui-sort" aria-label={active ? `${label}, sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`}
      onClick={() => setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}>
      {label}
      {active && <Icon icon={sort.dir === 'desc' ? ArrowDown : ArrowUp} />}
    </button>
  );
}

export default function Dashboard() {
  const [stats,        setStats]        = useState([]);
  const [depth,        setDepth]        = useState([]);
  const [activity,     setActivity]     = useState([]);
  const [incomplete,   setIncomplete]   = useState([]);
  const [qualSummary,  setQualSummary]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [failed,       setFailed]       = useState(false);
  const [postedFilter, setPostedFilter] = useState('week');
  const [postedCount,  setPostedCount]  = useState(null);
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();

  const fetchPostedCount = useCallback(async (filter) => {
    const since = getPostedSince(filter);
    const qs = since ? `?since=${since}` : '';
    try {
      const res = await api.get(`/api/dashboard/posted-count${qs}`);
      setPostedCount(res.data.count);
    } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [s, d, a, i, q] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/posting-depth'),
        api.get('/api/dashboard/recent-activity'),
        api.get('/api/dashboard/incomplete-alert'),
        api.get('/api/qual/summary'),
      ]);
      setStats(s.data);
      setDepth(d.data);
      setActivity(a.data);
      setIncomplete(i.data);
      setQualSummary(q.data);
    } catch (err) {
      console.error('Dashboard load failed:', err);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { fetchPostedCount(postedFilter); }, [postedFilter, fetchPostedCount]);

  const [pipeSort, setPipeSort] = useState({ col: 'client', dir: 'asc' });

  const sortedStats = useMemo(() => {
    if (!stats.length) return stats;
    const { col, dir } = pipeSort;
    return [...stats].sort((a, b) => {
      if (col === 'client') {
        const av = a.client.name.toLowerCase();
        const bv = b.client.name.toLowerCase();
        return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const aIsAI = a.client.productionTrack === 'AI';
      const bIsAI = b.client.productionTrack === 'AI';
      const av = (aIsAI ? a.ai : a.live)[col] ?? 0;
      const bv = (bIsAI ? b.ai : b.live)[col] ?? 0;
      return dir === 'asc' ? av - bv : bv - av;
    });
  }, [stats, pipeSort]);

  const isReviewer = hasRole(...QUAL_ROLES);
  const today = ymd(new Date());
  const weekEnd = (() => { const d = new Date(); d.setDate(d.getDate() + 6); return ymd(d); })();

  // ── The three lists ──────────────────────────────────────────────────────────────────────────────────────
  // Incomplete records, one entry per card or per series (D-120: a series' episodes appear once).
  const incompleteEntries = useMemo(() => groupEpisodes(incomplete).map(e => {
    const recs = e.kind === 'series' ? e.episodes : [e.record];
    const name = e.kind === 'series'
      ? `${clean(e.scriptName)} (${plural(e.episodes.length, 'episode')})`
      : clean(e.record.script?.scriptName) || 'Riff entry';
    const open = e.kind === 'series' ? () => navigate('/live') : () => navigate(`/production/${e.record.id}`);
    return { key: e.key, recs, name, client: clean(e.record.client?.name), open };
  }), [incomplete, navigate]);

  const nextDate = (recs, field, from, to) => recs.map(r => dayOf(r[field])).filter(d => d && d >= from && (!to || d <= to)).sort()[0] || null;

  const blockedRows = incompleteEntries.map(e => {
    const reshoot = nextDate(e.recs, 'shootScheduledDate', today);
    return { key: e.key, row: {
      title: e.name,
      meta: [e.client, reshoot ? `Reshoot ${shortDate(reshoot)}` : 'No reshoot scheduled'].filter(Boolean).join(', '),
      trailing: <Badge condition="Incomplete" />,
      onClick: e.open,
    } };
  });

  const dueRows = incompleteEntries.flatMap(e => {
    const shoot = nextDate(e.recs, 'shootScheduledDate', today, weekEnd);
    const edit = nextDate(e.recs, 'editScheduledDate', today, weekEnd);
    const when = [shoot && `Reshoot ${shortDate(shoot)}`, edit && `Edit ${shortDate(edit)}`].filter(Boolean);
    if (!when.length) return [];
    return [{ key: e.key, sort: [shoot, edit].filter(Boolean).sort()[0], row: {
      title: e.name, meta: [e.client, ...when].filter(Boolean).join(', '), trailing: <Badge condition="Incomplete" />, onClick: e.open,
    } }];
  }).sort((a, b) => a.sort.localeCompare(b.sort));

  const needsRows = useMemo(() => {
    const rows = [];
    if (isReviewer && qualSummary) {
      (qualSummary.scripts?.byClient || []).forEach(c => rows.push({ key: `qs:${c.clientId}`, row: {
        title: `${plural(c.count, 'script')} to review`, meta: clean(c.clientName),
        trailing: <Badge condition="Needs review" />, onClick: () => navigate('/qual?tab=scripts'),
      } }));
      const byClient = {};
      [['Live', qualSummary.liveVideos], ['AI', qualSummary.aiVideos]].forEach(([track, group]) => (group?.byClient || []).forEach(c => {
        const x = byClient[c.clientName] || (byClient[c.clientName] = { name: c.clientName, total: 0, parts: [] });
        x.total += c.count; x.parts.push(`${track} ${c.count}`);
      }));
      Object.values(byClient).sort((a, b) => b.total - a.total).forEach(c => rows.push({ key: `qv:${c.name}`, row: {
        title: `${plural(c.total, 'cut')} in qual`, meta: [clean(c.name), ...c.parts].join(', '),
        trailing: <StageChip stage="Qual" />, onClick: () => navigate('/qual?tab=videos'),
      } }));
    }
    incompleteEntries.forEach(e => {
      const mine = e.recs.filter(r => user && (r.shootLead?.id === user.id || r.editor?.id === user.id));
      if (!mine.length) return;
      const asLead = mine.some(r => r.shootLead?.id === user.id);
      rows.push({ key: `inc:${e.key}`, row: {
        title: e.name, meta: [e.client, asLead ? 'You lead the shoot' : 'You are the editor'].filter(Boolean).join(', '),
        trailing: <Badge condition="Incomplete" />, onClick: e.open,
      } });
    });
    return rows;
  }, [isReviewer, qualSummary, incompleteEntries, user, navigate]);

  // ── Kept from the old page: per-project tables ────────────────────────────────────────────────────────────
  const pipelineColumns = [
    { key: 'client', label: <SortHead col="client" label="Project" sort={pipeSort} setSort={setPipeSort} />, width: '22%', render: r => clean(r.client.name) },
    { key: 'type', label: 'Type', render: r => clean(r.client.productionTrack === 'AI' ? `${r.client.serviceType}, AI` : r.client.serviceType) },
    ...COLS.map(col => ({
      key: col, align: 'right',
      label: <SortHead col={col} label={col.charAt(0).toUpperCase() + col.slice(1)} sort={pipeSort} setSort={setPipeSort} />,
      render: r => {
        const isAI = r.client.productionTrack === 'AI';
        const n = isAI ? (col === 'filming' || col === 'filmed' ? null : r.ai[col]) : r.live[col];
        if (n == null) return null;
        return <span className={n ? 'ui-num' : 'ui-num ui-text-3'}>{n}</span>;
      },
    })),
  ];

  const depthRows = depth.filter(d => d.live.approved > 0 || d.ai.approved > 0).map(d => {
    const isAI = d.client.productionTrack === 'AI';
    return { id: d.client.id, name: d.client.name, track: isAI ? 'AI' : 'Live', count: isAI ? d.ai.approved : d.live.approved };
  });
  const depthColumns = [
    { key: 'name', label: 'Project', width: '34%', render: r => clean(r.name) },
    { key: 'track', label: 'Track' },
    { key: 'count', label: 'Approved', align: 'right', render: r => <span className="ui-num">{r.count}</span> },
    { key: 'days', label: 'Days of posts', align: 'right', render: r => <span className="ui-num">{`About ${plural(r.count, 'day')}`}</span> },
    { key: 'level', label: 'Depth', render: r => { const l = depthLevel(r.count); return <Badge tone={l.tone}>{l.label}</Badge>; } },
  ];

  const activityColumns = [
    { key: 'entity', label: 'Record', width: '18%', render: r => ENTITY_LABEL[r.entityType] || clean(r.entityType) },
    { key: 'from', label: 'From', render: r => <AuditValue entityType={r.entityType} value={r.oldValue} /> },
    { key: 'to', label: 'To', render: r => <AuditValue entityType={r.entityType} value={r.newValue} /> },
    { key: 'who', label: 'Changed by', render: r => clean(r.changedByMember?.name) },
    { key: 'when', label: 'When', render: r => <span className="ui-text-3">{timeAgo(r.changedAt)}</span> },
  ];

  const tableState = loading ? 'loading' : 'data';
  const dateLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  usePageHeader({
    title: 'Dashboard',
    meta: dateLabel,
    actions: <Button variant="secondary" icon={RefreshCw} loading={loading} onClick={() => { loadAll(); fetchPostedCount(postedFilter); }}>Refresh</Button>,
  });

  if (failed && !loading) {
    return (
      <div className="ui ui-page">
        <ErrorState title="The dashboard did not load" message="The server did not answer. Check your connection, then try again." onRetry={loadAll} />
      </div>
    );
  }

  return (
    <div className="ui ui-page">
      <div className="ui-cols ui-cols--3">
        <WorkSection title="Needs me" loading={loading} rows={needsRows}
          note={isReviewer ? 'Scripts and cuts waiting for review, and Incomplete productions you lead or edit.' : 'Incomplete productions you lead or edit.'}
          empty={{ message: 'Nothing is waiting on you. Open Scripts to pick up the next piece.', action: { label: 'Open scripts', icon: FileText, onClick: () => navigate('/scripts') } }} />
        <WorkSection title="Due this week" loading={loading} rows={dueRows}
          note="Reshoots and edits scheduled in the next 7 days."
          empty={{ message: 'Nothing is scheduled in the next 7 days. Set reshoot dates on the production calendar.', action: { label: 'Open calendar', icon: CalendarDays, onClick: () => navigate('/calendar') } }} />
        <WorkSection title="Blocked" loading={loading} rows={blockedRows}
          note="Productions marked Incomplete. Each needs a reshoot."
          empty={{ message: 'Nothing is blocked. When a shoot comes back Incomplete, it shows here until it is reshot.' }} />
      </div>

      {!isReviewer && qualSummary && (() => {
        const rows = [
          ...(qualSummary.scripts?.byClient || []).map(c => ({ id: `s:${c.clientId}`, kind: 'Scripts to review', client: c.clientName, count: c.count })),
          ...(qualSummary.liveVideos?.byClient || []).map(c => ({ id: `l:${c.clientId}`, kind: 'Live cuts in qual', client: c.clientName, count: c.count })),
          ...(qualSummary.aiVideos?.byClient || []).map(c => ({ id: `a:${c.clientId}`, kind: 'AI cuts in qual', client: c.clientName, count: c.count })),
        ];
        return (
          <section className="ui-section" aria-label="Qual queue">
            <div className="ui-section__head">
              <h2 className="ui-section__title">Qual queue</h2>
              <div className="ui-section__actions"><Button variant="quiet" onClick={() => navigate('/qual')}>Open qual</Button></div>
            </div>
            <div className="ui-region">
              <Table caption="Qual queue by client" rows={rows} state={tableState}
                columns={[
                  { key: 'kind', label: 'Waiting', width: '34%' },
                  { key: 'client', label: 'Client', render: r => clean(r.client) },
                  { key: 'count', label: 'Count', align: 'right', render: r => <span className="ui-num">{r.count}</span> },
                ]}
                empty={{ message: 'Nothing is in qual.' }} />
            </div>
          </section>
        );
      })()}

      <section className="ui-section" aria-label="Pipeline by project">
        <div className="ui-section__head">
          <h2 className="ui-section__title">Pipeline by project</h2>
          <div className="ui-section__actions ui-cluster">
            <span className="ui-dense ui-text-2">
              {postedCount === null ? 'Posted' : `${plural(postedCount, 'video')} posted ${POSTED_PHRASE[postedFilter]}`}
            </span>
            <Select aria-label="Posted period" value={postedFilter} onChange={e => setPostedFilter(e.target.value)} options={POSTED_OPTIONS} />
          </div>
        </div>
        <p className="ui-section__note">How many pieces each active project has at each stage, counted live.</p>
        <div className="ui-region">
          <Table caption="Pipeline by project" columns={pipelineColumns} rows={sortedStats} rowKey={r => r.client.id} state={tableState}
            empty={{ message: 'No active clients. Add or reactivate a client in Admin.' }} />
        </div>
      </section>

      <div className="ui-cols">
        <section className="ui-section" aria-label="Posting depth">
          <div className="ui-section__head"><h2 className="ui-section__title">Posting depth</h2></div>
          <p className="ui-section__note">Approved videos queued per project. 3 or fewer is low, 8 or more is good.</p>
          <div className="ui-region">
            <Table caption="Posting depth" columns={depthColumns} rows={depthRows} state={tableState}
              empty={{ message: 'No approved videos are queued. Approve cuts in Qual to build the queue.' }} />
          </div>
        </section>

        <section className="ui-section" aria-label="Recent activity">
          <div className="ui-section__head"><h2 className="ui-section__title">Recent activity</h2></div>
          <p className="ui-section__note">The last 10 stage and qual changes.</p>
          <div className="ui-region">
            <Table caption="Recent activity" columns={activityColumns} rows={activity.slice(0, 10)} state={tableState}
              empty={{ message: 'No stage changes yet. Changes show here as work moves.' }} />
          </div>
        </section>
      </div>
    </div>
  );
}
